// API functions for risk registers.
// Follows the same pattern as frameworkConfig.ts.

import type { ApiClient } from './client'

// --- Types ---

export interface Register {
  id: string
  parent_id: string | null
  path: string
  depth: number
  name: string
  description?: string | null
  owner_role_id: string
  owner_user_id?: string | null
  owner_display_name?: string | null
  risk_count: number
  child_count: number
  has_children: boolean
  created_at?: string
  updated_at?: string
}

export interface CreateRegisterRequest {
  parent_id: string
  name: string
  description?: string
  owner_role_id: string
}

export interface UpdateRegisterRequest {
  name?: string
  description?: string
}

// --- Tree node helper (client-side hierarchy from flat list) ---

export interface RegisterTreeNode extends Register {
  children: RegisterTreeNode[]
  expanded?: boolean
}

/** Build a tree from the flat array returned by GET /registers/tree (ordered by path). */
export function buildRegisterTree(registers: Register[]): RegisterTreeNode[] {
  const nodeMap = new Map<string, RegisterTreeNode>()
  const roots: RegisterTreeNode[] = []

  // Create nodes
  for (const reg of registers) {
    nodeMap.set(reg.id, { ...reg, children: [] })
  }

  // Wire parent-child
  for (const reg of registers) {
    const node = nodeMap.get(reg.id)!
    if (reg.parent_id && nodeMap.has(reg.parent_id)) {
      nodeMap.get(reg.parent_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  return roots
}

// --- API functions ---

export function registersApi(api: ApiClient) {
  return {
    tree: () => api.get<Register[]>('/registers/tree'),
    list: (search?: string) => {
      const params = search ? `?search=${encodeURIComponent(search)}` : ''
      return api.get<Register[]>(`/registers${params}`)
    },
    get: (id: string) => api.get<Register>(`/registers/${id}`),
    create: (data: CreateRegisterRequest) => api.post<Register>('/registers', data),
    update: (id: string, data: UpdateRegisterRequest) =>
      api.put<Register>(`/registers/${id}`, data),
    delete: (id: string) => api.del(`/registers/${id}`),
    children: (id: string) => api.get<Register[]>(`/registers/${id}/children`),
  }
}
